import Immutable from 'seamless-immutable';
import { createSelector } from 'reselect';
// ---- external imports ----

import { turnSelectors, levelSelectors } from '~/store/selectors';
import { getMonthsDifference, isDateBetween, isDateAfter } from '~/lib/helpers';
import { LEVELS_TO_UNLOCK_FEATURES } from '~/lib/constants';
// ---- internal imports ----


function getAll(state) {
	const level = levelSelectors.getLevelNumber(state);
	if (level < LEVELS_TO_UNLOCK_FEATURES.TRADESHOWS) {
		return [];
	}

	return state.tradeshows.all;
}

export function getHiredBooths(state) {
	return state.tradeshows.hiredBooths;
}

export function hasTradeshowStartingThisMonth(state) {
	const hiredBooths = getHiredBooths(state);
	const curTurnDate = turnSelectors.getTurnDate(state);

	return hiredBooths.some((h) => getMonthsDifference(new Date(curTurnDate), new Date(h.startingTurnDate)) === 0);
}

export const getAvailableForRent = createSelector(
	getAll,
	getHiredBooths,
	turnSelectors.getTurnDate,
	(all, hired, curTurnDate) => {
		if (!hired || hired.length === 0) {
			return all;
		}

		return all.filter(
			(tradeshow) => (
				hired.find(
					(h) => (
						h.tradeshow?.id === tradeshow?.id
						&& new Date(h.startingTurnDate) > new Date(curTurnDate)
					)
				) === undefined
			)
		);
	}
);

export const getUpcomingHiredBooths = createSelector(
	getHiredBooths,
	turnSelectors.getTurnDate,
	(hired, curTurnDate) => {
		if (!hired || !hired.length || hired.length === 0) {
			return null;
		}

		return hired.filter((h) => (
			getMonthsDifference(new Date(curTurnDate), new Date(h.startingTurnDate)) >= 0)
		);
	}
);

export const getNextHiredBooth = createSelector(
	getUpcomingHiredBooths,
	turnSelectors.isCustomersPhase,
	turnSelectors.getTurnDate,
	(upcomingTradeshows, isCustomersPhase, curTurnDate) => {
		if (!upcomingTradeshows || !upcomingTradeshows.length || upcomingTradeshows.length === 0) {
			return null;
		}

		const orderedUpcoming = Immutable.asMutable(upcomingTradeshows)
			.sort(
				(booth1, booth2) => (
					new Date(booth1?.startingTurnDate) - new Date(booth2?.startingTurnDate)
				)
			);

		const upcomingTradeshow = orderedUpcoming[0];

		if (isCustomersPhase
			&& getMonthsDifference(upcomingTradeshow.startingTurnDate, curTurnDate) === 0) {
			/**
			 * During customers phase, the upcomingTradeshow has 
			 * finished (getMonthsDifference == 0) and 
			 * should return the one after it (if exists).
			 */
			return orderedUpcoming.length > 1 ? orderedUpcoming[1] : null;
		}

		return upcomingTradeshow;
	}
);

export const didTradeshowStartedInCurrentTurn = createSelector(
	getUpcomingHiredBooths,
	getCustomers,
	turnSelectors.getTurnDate,
	turnSelectors.isCustomersPhase,
	(upcomingTradeshows, tradeshowCustomers, curTurnDate, isCustomersPhase) => {
		if (!upcomingTradeshows || !upcomingTradeshows.length || upcomingTradeshows.length === 0) {
			return false;
		}

		const orderedUpcoming = Immutable.asMutable(upcomingTradeshows)
			.sort(
				(booth1, booth2) => (
					new Date(booth1?.startingTurnDate) - new Date(booth2?.startingTurnDate)
				)
			);

		const upcomingTradeshow = orderedUpcoming[0];

		const isUpcomingTradeshowStartedInCurrentTurn =
			getMonthsDifference(upcomingTradeshow.startingTurnDate, curTurnDate) === 0;

		return isUpcomingTradeshowStartedInCurrentTurn;
	}
);

export const didTradeshowEndedInCurrentTurn = createSelector(
	getUpcomingHiredBooths,
	getCustomers,
	turnSelectors.getTurnDate,
	turnSelectors.isCustomersPhase,
	(upcomingTradeshows, tradeshowCustomers, curTurnDate, isCustomersPhase) => {
		if (!upcomingTradeshows || !upcomingTradeshows.length || upcomingTradeshows.length === 0) {
			return false;
		}

		const orderedUpcoming = Immutable.asMutable(upcomingTradeshows)
			.sort(
				(booth1, booth2) => (
					new Date(booth1?.startingTurnDate) - new Date(booth2?.startingTurnDate)
				)
			);

		const upcomingTradeshow = orderedUpcoming[0];

		const isUpcomingTradeshowStartedInCurrentTurn =
			getMonthsDifference(upcomingTradeshow.startingTurnDate, curTurnDate) === 0;

		const isTradeshowEnded = !tradeshowCustomers || !tradeshowCustomers?.length
			|| !tradeshowCustomers?.length === 0;

		return isUpcomingTradeshowStartedInCurrentTurn && isTradeshowEnded && isCustomersPhase;
	}
);

export function getTradeshowById(state, tradeshowId) {
	if (!tradeshowId) {
		return null;
	}

	const tradeshows = getAll(state);

	return tradeshows?.find(({ id }) => (id === tradeshowId));
}

export function getTotalPricesForFinishedTradeShowsBetweenDates(state, startDate, endDate) {
	const hiredTradeshows = getHiredBooths(state);

	if (!hiredTradeshows || !hiredTradeshows.length || hiredTradeshows.length === 0) {
		return 0;
	}

	return hiredTradeshows.reduce(
		(accTotalPrices, hiredTradeshow) => (
			accTotalPrices
			+ (
				isDateBetween(hiredTradeshow.startingTurnDate, startDate, endDate)
					? hiredTradeshow.payedPrice
					: 0
			)
		),
		0
	);
}

export function getPricesOfHiredTradeShowsAndNotFinishedBefore(state, date) {
	const hiredTradeshows = getHiredBooths(state);

	if (!hiredTradeshows || !hiredTradeshows.length || hiredTradeshows.length === 0) {
		return 0;
	}

	return hiredTradeshows.reduce(
		(accTotalPrices, hiredTradeshow) => (
			accTotalPrices
			+ (
				isDateAfter(hiredTradeshow.startingTurnDate, date)
					? hiredTradeshow.payedPrice
					: 0
			)
		),
		0
	);
}

export function getCustomers(state) {
	return state.tradeshows.tradeshowCustomers;
}
